import { useDeferredValue, useState } from 'react'
import { ScanLine, Search, Trash2, Undo2 } from 'lucide-react'
import type { LabelUsage, ListLabelsQuery, ProductLabel } from '@/api/productLabelsApi'
import { useProductLabels } from '@/hooks/useProductLabels'
import { useTableColumns } from '@/hooks/useTableColumns'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import { usePermission, PERMISSIONS } from '@/lib/permissions'
import { USAGE_LABEL, PRINT_KIND_LABEL, labelActions, postPrintText, signedText } from '@/lib/productLabels'
import { cn } from '@/lib/utils'
import { ColumnCells, ResizableTableHead } from '@/components/table'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table'
import { VerifyLabelDialog } from './VerifyLabelDialog'
import { LabelUsageDialog } from './LabelUsageDialog'

// The legacy label screen's printed-labels list (z8nrz7dt48): one row per
// physical label, with its usage and its pre- and post-print signatures.
const LABELS_TABLE_ID = 'product-labels'

const LABELS_COLUMNS: TableColumnDef[] = [
  { id: 'number', header: '#', locked: true, defaultWidth: 70 },
  { id: 'usage', header: 'Usage', defaultWidth: 130 },
  { id: 'printed', header: 'Date printed', defaultWidth: 170 },
  { id: 'printedBy', header: 'Printed by', defaultWidth: 140 },
  { id: 'labelType', header: 'Label type', defaultWidth: 160 },
  { id: 'din', header: 'DIN', defaultWidth: 160 },
  { id: 'productCode', header: 'Product code', defaultWidth: 120 },
  { id: 'patient', header: 'Patient', defaultWidth: 160 },
  { id: 'prePrint', header: 'Pre-print verification by', defaultWidth: 210 },
  { id: 'postPrint', header: 'Post-print verification by', defaultWidth: 210 },
  { id: 'actions', header: 'Actions', locked: true, defaultWidth: 130 },
]

const USAGE_CLASS: Record<LabelUsage, string> = {
  pending: 'border-amber-300 bg-amber-50 text-amber-800',
  applied: 'border-emerald-300 bg-emerald-50 text-emerald-800',
  discarded: 'border-red-200 bg-red-50 text-red-700',
  unused: 'border-gray-300 bg-gray-50 text-gray-700',
}

/**
 * Printed product labels, newest first, filterable to the ones still waiting
 * for post-print verification. Each row verifies (scan + sign), discards
 * (reason + sign) or returns a label unused (sign). Scoped by `scope` — one
 * form's labels in the run dialog, every label on the Products page.
 */
export function ProductLabelsTable({ scope = {} }: { scope?: Pick<ListLabelsQuery, 'submissionId' | 'productId' | 'procedureId'> }) {
  const cols = useTableColumns(LABELS_TABLE_ID, LABELS_COLUMNS)
  const { can } = usePermission()
  const canManage = can(PERMISSIONS.productsManage)
  const [q, setQ] = useState('')
  const [unverified, setUnverified] = useState(false)
  const query = useDeferredValue(q.trim())
  const { data, isLoading, error } = useProductLabels({ ...scope, q: query || undefined, unverified })

  const [verifying, setVerifying] = useState<ProductLabel | null>(null)
  const [changing, setChanging] = useState<{ label: ProductLabel; usage: 'discarded' | 'unused' } | null>(null)

  const span = cols.visible.length
  return (
    <>
      <div className='mb-3 flex flex-wrap items-center gap-4'>
        <div className='relative w-full max-w-sm'>
          <Search className='pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground' />
          <Input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder='Search by label #, DIN, product code or patient…'
            aria-label='Search labels'
            className='bg-white pl-8'
          />
        </div>
        <label className='flex cursor-pointer items-center gap-2 text-sm'>
          <Checkbox checked={unverified} onCheckedChange={c => setUnverified(c === true)} aria-label='Un-verified labels only' />
          Un-verified labels only
        </label>
      </div>
      <div className='overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm'>
        <Table>
          <TableHeader>
            <TableRow>
              {cols.visible.map(({ def }) => (
                <ResizableTableHead
                  key={def.id}
                  columnId={def.id}
                  columns={cols}
                  label={def.header}
                  resizable={def.id !== 'actions'}
                >
                  {def.header}
                </ResizableTableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <MessageRow span={span}>Loading…</MessageRow>
            ) : error ? (
              <MessageRow span={span}>Could not load labels.</MessageRow>
            ) : (data?.length ?? 0) === 0 ? (
              <MessageRow span={span}>
                {query || unverified
                  ? 'No labels match.'
                  : 'No labels printed yet. Each print of a product label is listed here.'}
              </MessageRow>
            ) : (
              data!.map(l => {
                const actions = labelActions(l.usage)
                return (
                  <TableRow key={l.id}>
                    <ColumnCells
                      columns={cols}
                      cells={{
                        number: <TableCell className='font-medium tabular-nums'>{l.number}</TableCell>,
                        usage: (
                          <TableCell>
                            <span
                              className={cn('rounded-full border px-2 py-0.5 text-[11px] font-medium', USAGE_CLASS[l.usage])}
                              title={l.usageReason ? `${USAGE_LABEL[l.usage]}: ${l.usageReason}` : undefined}
                            >
                              {USAGE_LABEL[l.usage]}
                            </span>
                          </TableCell>
                        ),
                        printed: (
                          <TableCell className='text-muted-foreground'>
                            <div>{new Date(l.printedAt).toLocaleString()}</div>
                            <div className='text-[11px]'>
                              {PRINT_KIND_LABEL[l.printKind]}
                              {l.replaces ? ` · replaces #${l.replaces.number}` : ''}
                            </div>
                          </TableCell>
                        ),
                        printedBy: <TableCell>{l.printedByName ?? l.printedBy}</TableCell>,
                        labelType: <TableCell>{l.labelType}</TableCell>,
                        din: <TableCell className='font-mono text-xs'>{l.din}</TableCell>,
                        productCode: <TableCell className='font-mono text-xs'>{l.productCode || '—'}</TableCell>,
                        patient: <TableCell>{l.product?.patientName ?? l.product?.patientMrn ?? '—'}</TableCell>,
                        prePrint: (
                          <TableCell className='text-xs'>{signedText(l.printedByName ?? l.printedBy, l.printedAt)}</TableCell>
                        ),
                        postPrint: (
                          <TableCell
                            className={cn('text-xs', l.usage === 'pending' && 'font-medium text-amber-700')}
                          >
                            {postPrintText(l)}
                          </TableCell>
                        ),
                        actions: (
                          <TableCell>
                            {canManage && (
                              <div className='flex items-center gap-1'>
                                {actions.verify && (
                                  <Button
                                    variant='ghost'
                                    size='sm'
                                    className='h-8 w-8 p-0'
                                    title={`Verify label #${l.number} after printing`}
                                    aria-label={`Verify label #${l.number}`}
                                    onClick={() => setVerifying(l)}
                                  >
                                    <ScanLine className='h-4 w-4' />
                                  </Button>
                                )}
                                {actions.returnUnused && (
                                  <Button
                                    variant='ghost'
                                    size='sm'
                                    className='h-8 w-8 p-0'
                                    title={`Return label #${l.number} unused`}
                                    aria-label={`Return label #${l.number} unused`}
                                    onClick={() => setChanging({ label: l, usage: 'unused' })}
                                  >
                                    <Undo2 className='h-4 w-4' />
                                  </Button>
                                )}
                                {actions.discard && (
                                  <Button
                                    variant='ghost'
                                    size='sm'
                                    className='h-8 w-8 p-0'
                                    title={`Discard label #${l.number}`}
                                    aria-label={`Discard label #${l.number}`}
                                    onClick={() => setChanging({ label: l, usage: 'discarded' })}
                                  >
                                    <Trash2 className='h-4 w-4 text-destructive' />
                                  </Button>
                                )}
                              </div>
                            )}
                          </TableCell>
                        ),
                      }}
                    />
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>
      <VerifyLabelDialog label={verifying} onClose={() => setVerifying(null)} />
      <LabelUsageDialog
        label={changing?.label ?? null}
        usage={changing?.usage ?? 'discarded'}
        onClose={() => setChanging(null)}
      />
    </>
  )
}

function MessageRow({ children, span }: { children: React.ReactNode; span: number }) {
  return (
    <TableRow>
      <TableCell colSpan={span} className='py-12 text-center text-sm text-muted-foreground'>
        {children}
      </TableCell>
    </TableRow>
  )
}
